import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Building2, CheckCircle2, AlertTriangle, RefreshCw, Search, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { LaadBalk } from "@/components/LaadIndicator";

function LedgerAanmakenDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetKey, setPresetKey] = useState("0"); // index in presets
  const [naam, setNaam] = useState("");
  const [code, setCode] = useState("");
  const [customRgs, setCustomRgs] = useState("");
  const [customType, setCustomType] = useState("revenue");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/moneybird/ledger-accounts/presets")
      .then(({ data }) => setPresets(data || []))
      .catch(() => setPresets([]));
  }, [open]);

  const isCustom = presetKey === "custom";
  const gekozen = !isCustom && presets[Number(presetKey)];

  const opslaan = async () => {
    if (!naam.trim()) { toast.error("Naam is verplicht"); return; }
    const rgs = isCustom ? customRgs.trim() : (gekozen?.rgs_code || "");
    const type = isCustom ? customType : (gekozen?.account_type || "revenue");
    if (!rgs) { toast.error("Kies een preset of vul een RGS-code in"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/moneybird/ledger-accounts", {
        name: naam.trim(),
        account_type: type,
        account_id: code.trim() || null,
        rgs_code: rgs,
      });
      toast.success(`Grootboek "${data.name}" aangemaakt`);
      setOpen(false);
      setNaam(""); setCode(""); setCustomRgs("");
      setPresetKey("0");
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="ledger-nieuw-btn">
          <Plus className="h-4 w-4 mr-1" /> Nieuw grootboek
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nieuw grootboek in Moneybird aanmaken</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Soort</Label>
            <Select value={presetKey} onValueChange={setPresetKey}>
              <SelectTrigger data-testid="ledger-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map((p, i) => (
                  <SelectItem key={i} value={String(i)}>{p.label}</SelectItem>
                ))}
                <SelectItem value="custom">— Geavanceerd: eigen RGS-code —</SelectItem>
              </SelectContent>
            </Select>
            {!isCustom && gekozen && (
              <div className="text-xs text-muted-foreground mt-1">
                Type: <span className="font-mono">{gekozen.account_type}</span> · RGS: <span className="font-mono">{gekozen.rgs_code}</span>
              </div>
            )}
          </div>
          {isCustom && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">RGS-code (3.5)</Label>
                <Input value={customRgs} onChange={(e) => setCustomRgs(e.target.value)}
                       placeholder="Bijv. WOmzNohOlh" data-testid="ledger-custom-rgs" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Account type</Label>
                <Select value={customType} onValueChange={setCustomType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["revenue","direct_costs","expenses","other_income_expenses","current_assets","non_current_assets","equity","provisions","current_liabilities","non_current_liabilities"].map(t=>(
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground">Naam</Label>
            <Input value={naam} onChange={(e) => setNaam(e.target.value)}
                   placeholder="Bijv. Omzet Nextbrush hoog" data-testid="ledger-naam" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Grootboekcode (optioneel)</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="Bijv. 81100.10" data-testid="ledger-code" />
            <div className="text-xs text-muted-foreground mt-1">
              Vrij te kiezen — bijv. je eigen nummeringsschema uit Excel.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={busy} data-testid="ledger-opslaan">
            {busy ? "Aanmaken…" : "Aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerBewerken({ ledger, onDone }) {
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState(ledger.name || "");
  const [code, setCode] = useState(ledger.code || "");
  const [busy, setBusy] = useState(false);

  const opslaan = async () => {
    if (!naam.trim()) { toast.error("Naam is verplicht"); return; }
    setBusy(true);
    try {
      await api.patch(`/moneybird/ledger-accounts/${ledger.id}`,
                       { name: naam.trim(), account_id: code.trim() });
      toast.success("Bijgewerkt");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Bewerken"
                data-testid={`ledger-edit-${ledger.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Grootboek bewerken</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Naam</Label>
            <Input value={naam} onChange={(e) => setNaam(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Grootboekcode</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={busy}>Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerVerwijderen({ ledger, onDone }) {
  const [busy, setBusy] = useState(false);
  const verwijder = async () => {
    if (!window.confirm(`Grootboek "${ledger.name}" definitief verwijderen uit Moneybird?\n\nLukt alleen als er geen boekingen op staan.`)) return;
    setBusy(true);
    try {
      await api.delete(`/moneybird/ledger-accounts/${ledger.id}`);
      toast.success("Verwijderd");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt (mogelijk al in gebruik)");
    } finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" size="icon" onClick={verwijder} disabled={busy}
            className="text-destructive hover:text-destructive"
            title="Verwijderen"
            data-testid={`ledger-del-${ledger.id}`}>
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  );
}

export default function InstellingenMoneybird() {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [ledgerZoek, setLedgerZoek] = useState("");
  const [ledgerType, setLedgerType] = useState("alle");
  const [busy, setBusy] = useState(false);

  const laden = async () => {
    try {
      const { data } = await api.get("/moneybird/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moneybird-status ophalen mislukt");
    }
    try {
      const { data } = await api.get("/moneybird/financial-accounts");
      setAccounts(data || []);
    } catch { /* niet-fataal — mogelijk lege administratie */ }
    try {
      const { data } = await api.get("/moneybird/ledger-accounts");
      setLedgers(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grootboeken ophalen mislukt");
    }
  };
  useEffect(() => { laden(); }, []);

  const opslaan = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post("/moneybird/config", patch);
      setStatus((s) => ({ ...s, config: data.config }));
      toast.success("Opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  const syncTarieven = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/moneybird/tax-rates/sync");
      toast.success(`BTW-tarieven bijgewerkt: ${Object.keys(data.gecached).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync mislukt");
    } finally { setBusy(false); }
  };

  const ok = status?.ping?.ok;
  const cfg = status?.config || {};
  const geselecteerd = useMemo(() => new Set(cfg.ledger_ids || []), [cfg.ledger_ids]);

  const ledgerTypes = useMemo(() => {
    const s = new Set(ledgers.map(l => l.type).filter(Boolean));
    return ["alle", ...Array.from(s).sort()];
  }, [ledgers]);

  const zichtbaar = useMemo(() => {
    let l = ledgers;
    if (ledgerType !== "alle") l = l.filter(x => x.type === ledgerType);
    if (ledgerZoek.trim()) {
      const t = ledgerZoek.trim().toLowerCase();
      l = l.filter(x => (x.name || "").toLowerCase().includes(t)
        || (x.code || "").toLowerCase().includes(t));
    }
    return l.slice().sort((a, b) => (a.code || "").localeCompare(b.code || "")
      || (a.name || "").localeCompare(b.name || ""));
  }, [ledgers, ledgerType, ledgerZoek]);

  const toggleLedger = async (id, checked) => {
    const set = new Set(geselecteerd);
    if (checked) set.add(id); else set.delete(id);
    await opslaan({ ledger_ids: Array.from(set) });
  };

  const bulkSelecteer = async (idsToCheck) => {
    const set = new Set(geselecteerd);
    idsToCheck.forEach(id => set.add(id));
    await opslaan({ ledger_ids: Array.from(set) });
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="instellingen-moneybird-page">
      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Moneybird</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Verkoopfacturen, creditfacturen en betalingen worden automatisch naar Moneybird geboekt
          zodra ze in het CRM worden verstuurd of als betaald gemarkeerd.
        </p>
      </div>

      {!status ? (
        <LaadBalk label="Moneybird-status laden" />
      ) : (
        <>
          <div className="bg-card border border-border rounded-xl p-6 space-y-4"
               data-testid="moneybird-status-blok">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-medium">Verbinding</h2>
              {ok ? (
                <span className="ml-auto inline-flex items-center gap-1 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"
                      data-testid="moneybird-verbinding-ok">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Verbonden
                </span>
              ) : (
                <span className="ml-auto inline-flex items-center gap-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5"
                      data-testid="moneybird-verbinding-fout">
                  <AlertTriangle className="h-3.5 w-3.5" /> {status?.ping?.reden || "Niet verbonden"}
                </span>
              )}
            </div>
            {ok && status.ping.administraties?.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Administratie: <span className="text-foreground font-medium">
                  {status.ping.administraties[0].naam}</span> (ID {status.ping.administration_id})
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-6 space-y-5"
               data-testid="moneybird-config-blok">
            <h2 className="text-lg font-medium">Instellingen</h2>

            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="font-medium">Automatisch pushen</div>
                <div className="text-sm text-muted-foreground">
                  Bij het versturen van een factuur wordt hij ook direct in Moneybird geboekt.
                  Zet je dit uit, dan kun je facturen handmatig pushen via de knop op de factuur.
                </div>
              </div>
              <Switch checked={!!cfg.auto_push} onCheckedChange={(v) => opslaan({ auto_push: v })}
                      disabled={busy} data-testid="moneybird-auto-push" />
            </div>

            <div>
              <div className="font-medium mb-1">Bank/kasrekening voor betalingen</div>
              <div className="text-sm text-muted-foreground mb-2">
                Kies welke Moneybird-rekening gebruikt wordt wanneer een factuur als betaald wordt gemarkeerd.
                Laat leeg om Moneybird te laten beslissen op basis van eerdere betalingen.
              </div>
              <Select value={cfg.financial_account_id || "geen"}
                      onValueChange={(v) => opslaan({ financial_account_id: v === "geen" ? "" : v })}>
                <SelectTrigger className="w-full" data-testid="moneybird-financial-account">
                  <SelectValue placeholder="— geen selectie —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="geen">— geen selectie (Moneybird kiest) —</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} {a.identifier ? `· ${a.identifier}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={syncTarieven} disabled={busy}
                      data-testid="moneybird-refresh-tax">
                <RefreshCw className="h-4 w-4 mr-1" /> BTW-tarieven verversen
              </Button>
              <span className="text-xs text-muted-foreground">
                Nodig als je in Moneybird tarieven toevoegt of aanpast.
              </span>
            </div>
          </div>

          {/* Grootboek-selectie */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4"
               data-testid="moneybird-ledgers-blok">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-medium">Grootboek-selectie voor producten</h2>
                <p className="text-sm text-muted-foreground">
                  Vink de grootboeken aan die je bij producten wilt kunnen kiezen. Alleen aangevinkte
                  grootboeken verschijnen in de productdropdown. Laat je alles leeg, dan tonen we
                  automatisch alle omzet-grootboeken.
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                <span data-testid="ledger-count">{geselecteerd.size}</span> geselecteerd · {ledgers.length} totaal
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Zoek op naam of code…" value={ledgerZoek}
                       onChange={(e) => setLedgerZoek(e.target.value)}
                       className="pl-8" data-testid="ledger-zoek" />
              </div>
              <Select value={ledgerType} onValueChange={setLedgerType}>
                <SelectTrigger className="w-56" data-testid="ledger-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ledgerTypes.map(t => (
                    <SelectItem key={t} value={t}>{t === "alle" ? "Alle typen" : t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm"
                      onClick={() => bulkSelecteer(zichtbaar.filter(l => l.type === "revenue").map(l => l.id))}
                      disabled={busy || zichtbaar.filter(l => l.type === "revenue").length === 0}
                      data-testid="ledger-alle-omzet">
                Alle omzet-grootboeken selecteren
              </Button>
            </div>

            <div className="border border-border rounded-lg divide-y divide-border max-h-[500px] overflow-y-auto">
              {zichtbaar.map(l => (
                <label key={l.id}
                       className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/40 cursor-pointer"
                       data-testid={`ledger-rij-${l.id}`}>
                  <Checkbox checked={geselecteerd.has(l.id)}
                            onCheckedChange={(v) => toggleLedger(l.id, !!v)}
                            data-testid={`ledger-checkbox-${l.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.code ? <span className="font-mono">{l.code}</span> : "geen code"}
                      {l.type ? <span> · {l.type}</span> : ""}
                    </div>
                  </div>
                </label>
              ))}
              {zichtbaar.length === 0 && (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  Geen grootboeken gevonden — pas je zoek/filter aan.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
